import React, { useEffect, useRef, useState } from "react";
import styles from "./videos.module.css";
import { Typography, Empty } from "antd";
import { useParams } from "react-router-dom";
import OrderService from "../../../../services/OrderService";
import OrderModel from "../../../../models/Order";
import ClaimModal from "./ClaimModal";

const { Title } = Typography;

const Videos: React.FC = () => {
  const { assemblyCode } = useParams<{ assemblyCode: string }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [order, setOrder] = useState<OrderModel | null>(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [lastPos, setLastPos] = useState<{ x: number; y: number } | null>(null);
  const [lastDistance, setLastDistance] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const handleTimeUpdate = () => {
    if (!videoRef.current || !videoRef.current.duration) return;

    const percent =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(percent);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoRef.current) return;
    const percent = parseFloat(e.target.value);
    const newTime = (percent / 100) * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
    setProgress(percent); // actualiza la barra también
  };

  // Pinch zoom + pan
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current || !videoRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const videoRect = videoRef.current.getBoundingClientRect();

    if (e.touches.length === 2) {
      // ----- ZOOM -----
      const [touch1, touch2] = [e.touches[0], e.touches[1]];
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (lastDistance) {
        const delta = distance / lastDistance;
        let newScale = Math.min(Math.max(1, scale * delta), 4);
        setScale(newScale);

        // Cuando hago zoom, reseteo un poco la posición si está fuera de límites
        const maxX = (videoRect.width * newScale - containerRect.width) / 2;
        const maxY = (videoRect.height * newScale - containerRect.height) / 2;

        setTranslate((prev) => ({
          x: Math.min(Math.max(prev.x, -maxX), maxX),
          y: Math.min(Math.max(prev.y, -maxY), maxY),
        }));
      }
      setLastDistance(distance);
    } else if (e.touches.length === 1 && scale > 1) {
      // ----- PAN -----
      const touch = e.touches[0];
      if (lastPos) {
        const dx = touch.clientX - lastPos.x;
        const dy = touch.clientY - lastPos.y;

        const maxX = (videoRect.width * scale - containerRect.width) / 2;
        const maxY = (videoRect.height * scale - containerRect.height) / 2;

        setTranslate((prev) => ({
          x: Math.min(Math.max(prev.x + dx, -maxX), maxX),
          y: Math.min(Math.max(prev.y + dy, -maxY), maxY),
        }));
      }
      setLastPos({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = () => {
    setLastDistance(null);
    setLastPos(null);
  };

  // Fullscreen del contenedor
  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current) {
      containerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen().catch(console.error);
    }
  };

  // Detectamos cambios de fullscreen para ajustar estado
  useEffect(() => {
    const handleFsChange = () => {
      const isFs = !!document.fullscreenElement;
      setIsFullscreen(isFs);

      if (!isFullscreen) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      }
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  useEffect(() => {
    let scale = 1;
    let initialDistance: number | null = null;
    const video = document.getElementById("zoomable-video");
    const container = document.getElementById("video-zoom-container");

    function getDistance(touches: TouchList) {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function handleTouchStart(e: TouchEvent) {
      if (e.touches.length === 2) {
        initialDistance = getDistance(e.touches);
      }
    }

    function handleTouchMove(e: TouchEvent) {
      if (e.touches.length === 2 && initialDistance) {
        const currentDistance = getDistance(e.touches);
        let newScale = (currentDistance / initialDistance) * scale;
        newScale = Math.max(1, Math.min(newScale, 3)); // Limita el zoom entre 1x y 3x
        if (video) video.style.transform = `scale(${newScale})`;
      }
    }

    function handleTouchEnd(e: TouchEvent) {
      if (video) {
        const currentTransform = video.style.transform;
        const match = currentTransform.match(/scale\(([\d.]+)\)/);
        scale = match ? parseFloat(match[1]) : 1;
      }
      initialDistance = null;
    }

    if (container) {
      container.addEventListener("touchstart", handleTouchStart, {
        passive: false,
      });
      container.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      container.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });
    }
    return () => {
      if (container) {
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
      }
    };
  }, []);

  useEffect(() => {
    if (assemblyCode) {
      setLoading(true);
      OrderService.getOrder(assemblyCode).then((order) => {
        setLoading(false);
        if (!order) {
          setOrder(null);
          return;
        }
        setOrder(order);
      });
    } else {
      setLoading(false);
    }
  }, [assemblyCode]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoaded = () => setProgress(0);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoaded);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoaded);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
    };
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <Title level={4}>Cargando pedido...</Title>
      </div>
    );
  }

  if (!order) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Empty
          description={`El codigo del pedido ${assemblyCode} no se encontro`}
        />
      </div>
    );
  }

  return (
    <>
      <div className={`${styles.mainContainer} container`}>
        <div className={`${styles.rowTitle} row`}>
          <div className={`${styles.colTitle} col-12 mt-4`}>
            <h1 className={`${styles.title}`}>
              Sistema de Entrega Certificada y Trazabilidad Logística
            </h1>
          </div>
        </div>

        <div className={`${styles.rowSubtitle} row justify-content-center`}>
          <div className={`${styles.colSubtitle} col-10 mt-3`}>
            <p className={`${styles.subtitle}`}>
              Rastrea tus pedidos con completa transparencia y actualizaciones
              en tiempo real
            </p>
          </div>
        </div>

        <div className={`${styles.rowContent} row`}>
          <div className={`${styles.colDetail} col-12 col-md-5 my-2 py-3`}>
            <div className={`${styles.rowDetailTitle} row mt-3 ms-1`}>
              <div
                className={`${styles.colDetailTitle} col d-flex align-items-center`}
              >
                <img src="/ic_box.svg" alt="" />
                <h2 className="m-0 ms-2">Detalles del pedido</h2>
              </div>
            </div>
            <div className="row mt-5 ms-1">
              <div className="col">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#3a3a3a",
                  }}
                >
                  Número de cliente:
                </span>
              </div>
              <div className="col d-flex justify-content-end me-4">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "clamp(1.0rem, 3vw, 1.2rem)",
                  }}
                >
                  {order.client_code}
                </span>
              </div>
            </div>
            <hr />
            <div className="row mt-4 ms-1">
              <div className="col">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#3a3a3a",
                  }}
                >
                  Número de consolidado:
                </span>
              </div>
              <div className="col d-flex justify-content-end me-4">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "clamp(1.0rem, 3vw, 1.2rem)",
                  }}
                >
                  {order.consolidated_number}
                </span>
              </div>
            </div>

            <hr />

            <div className="row mt-4 ms-1">
              <div className="col">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#3a3a3a",
                  }}
                >
                  Número/s de pedido:
                </span>
              </div>
              <div className="col d-flex flex-column align-items-end me-4">
                {order.order_data.map((o, index) => (
                  <span
                    key={index}
                    style={{
                      display: "block",
                      fontWeight: "bold",
                      fontSize: "clamp(1.0rem, 3vw, 1.2rem)",
                      marginBottom: "6px",
                    }}
                  >
                    {o.numeroComprobante}
                  </span>
                ))}
              </div>
            </div>

            <hr />

            <div className="row mt-4 ms-1">
              <div className="col">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                    color: "#3a3a3a",
                  }}
                >
                  Fecha del pedido:
                </span>
              </div>
              <div className="col d-flex justify-content-end me-4">
                <span
                  style={{
                    fontWeight: "bold",
                    fontSize: "clamp(1.0rem, 3vw, 1.2rem)",
                  }}
                >
                  {order.timestamp_start}
                </span>
              </div>
            </div>
          </div>

          <div className={`${styles.colVideo} col-12 col-md-5 my-2 py-2`}>
            <div className={`${styles.rowVideoTitle} row mt-3 ms-1`}>
              <div
                className={`${styles.colVideoTitle} col d-flex align-items-top align-items-center`}
              >
                <img src="/ic_check.svg" alt="" />
                <h2 className="m-0 ms-2">
                  Video del momento cuando este pedido fue preparado
                </h2>
              </div>
            </div>

            <div className="row">
              <div className="col mt-4">
                <div
                  ref={containerRef}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    overflow: "hidden",
                    touchAction: "none",
                    width: isFullscreen ? "100vw" : "100%",
                    height: isFullscreen ? "100vh" : "auto",
                    maxWidth: isFullscreen ? "100vw" : "600px",
                    position: isFullscreen ? "fixed" : "relative",
                    top: isFullscreen ? 0 : undefined,
                    left: isFullscreen ? 0 : undefined,
                    borderRadius: isFullscreen ? 0 : 12,
                    boxShadow: isFullscreen
                      ? "none"
                      : "0 4px 12px rgba(0,0,0,0.15)",
                    border: isFullscreen ? "none" : "2px #dedede solid",
                    marginTop: 10,
                    backgroundColor: "black",
                    zIndex: isFullscreen ? 9999 : "auto",
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    // controls
                    src={`https://api.algolabs.ai/video/videos/${assemblyCode}`}
                    onTimeUpdate={() => {
                      if (!videoRef.current || !videoRef.current.duration)
                        return;
                      const percent =
                        (videoRef.current.currentTime /
                          videoRef.current.duration) *
                        100;
                      setProgress(percent);
                    }}
                    onLoadedMetadata={() => {
                      setProgress(0);
                      console.log(
                        "metadata cargada, duración:",
                        videoRef.current?.duration
                      );
                    }}
                    onPlay={() => {
                      setIsPlaying(true);
                    }}
                    onPause={() => {
                      setIsPlaying(false);
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                      transition: "transform 0.1s",
                      touchAction: "none",
                    }}
                  >
                    Tu navegador no soporta el video.
                  </video>

                  {/* Botón fullscreen */}
                  <button
                    onClick={toggleFullscreen}
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      zIndex: 10,
                      padding: isFullscreen ? "4px 8px" : "6px 12px",
                      borderRadius: 6,
                      background: isFullscreen ? "#2a6eb5b3" : "#2A6EB5",
                      color: "white",
                      border: "none",
                      fontSize: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    <img
                      src={
                        isFullscreen ? "/ic_minimize.svg" : "/ic_maximize.svg"
                      }
                      alt=""
                    />
                  </button>

                  {/* Controles personalizados */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 10,
                      left: 10,
                      right: 10,
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      zIndex: 10,
                    }}
                  >
                    <button
                      onClick={() => {
                        if (videoRef.current?.paused) {
                          videoRef.current.play();
                          setIsPlaying(true);
                        } else {
                          videoRef.current?.pause();
                          setIsPlaying(false);
                        }
                      }}
                      style={{
                        background: "#2A6EB5",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 12px",
                        display: "flex",
                      }}
                    >
                      <img
                        src={isPlaying ? "/ic_pause.svg" : "/ic_play.svg"}
                        alt=""
                      />
                    </button>

                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progress}
                      onChange={handleSeek}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* src={`https://api.algolabs.ai/video/videos/${assemblyCode}`} */}

        <div className={`${styles.rowForm} row justify-content-evenly mt-5 `}>
          <div className={`${styles.colForm} col-12 my-2`}>
            <div className={`${styles.rowFormTitle} row mt-3 ms-1`}>
              <div
                className={`${styles.colFormTitle} col d-flex align-items-center`}
              >
                <img src="/ic_form.svg" alt="" />
                <h2 className="m-0 ms-2">Formulario de reclamo</h2>
              </div>
            </div>

            <div className="row mt-3">
              <div className="col ms-3">
                <p className="" style={{ fontSize: "18px" }}>
                  Complete el siguiente formulario para enviar su reclamo o
                  consulta
                </p>
              </div>
            </div>

            {/* <<<<<Form>>>>> */}
            <ClaimModal
              isVisible={modalOpen}
              onClose={() => setModalOpen(false)}
            />
          </div>
        </div>

        <div className={`} row justify-content-evenly mt-5 `}>
          <hr className="m-0" />
          <div className={`d-flex justify-content-center col-11 my-4`}>
            <p
              className="m-0"
              style={{ fontSize: "18px", textAlign: "center" }}
            >
              Sistema de Entrega Certificada • Garantizando transparencia y
              calidad en cada entrega
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Videos;
